// ============================================================================
// PUENTE QR DE EVIDENCIA — página PÚBLICA /evidencia/:token (sin login).
// La compu de recepción muestra el QR (PhotoEvidence → "QR celular"); este lado
// corre en el celular que lo escanea: toma/elige la foto, la comprime y la sube
// a POST /api/tasks/evidence/:token. Port de la página de la agenda dental con
// los tokens de Let's Move (F4 plan de tareas, 17-ago-2026).
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, CheckCircle2, XCircle, Loader2, ClipboardList, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { fileToDataUrl } from '../../utils/imageCompress';

interface Info { title: string; status: string; requires_photo: 0 | 1; has_photo: boolean; }
type Step = 'cargando' | 'lista' | 'subiendo' | 'exito' | 'muerto';

export default function EvidencePage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<Info | null>(null);
  const [step, setStep] = useState<Step>('cargando');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const galRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch(`/api/tasks/evidence/${token}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'Enlace inválido o vencido');
        setInfo(j); setStep('lista');
      })
      .catch((e) => { setError(e.message); setStep('muerto'); });
  }, [token]);

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setError('');
    setStep('subiendo');
    try {
      const dataUrl = await fileToDataUrl(f);
      setPreview(dataUrl);
      const r = await fetch(`/api/tasks/evidence/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 409) {
        // Token vencido/borrado o tarea ya resuelta: estado TERMINAL — generar otro QR allá.
        setError(j.error || 'Enlace vencido'); setStep('muerto'); return;
      }
      if (!r.ok) throw new Error(j.error || 'No se pudo subir la foto');
      setStep('exito');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir la foto');
      setStep('lista');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 text-center shadow-xl">
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
        <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={pick} />

        {step === 'cargando' && (
          <div className="flex flex-col items-center gap-3 py-8 text-content-2">
            <Loader2 size={28} className="animate-spin" /> Verificando enlace…
          </div>
        )}

        {step === 'muerto' && (
          <div className="py-6">
            <XCircle size={40} className="mx-auto mb-3 text-rose-500" />
            <h1 className="text-lg font-semibold text-content">Enlace inválido o vencido</h1>
            <p className="mt-1 text-sm text-content-3">{error || 'Genera otro QR desde el panel de tareas y vuelve a escanearlo.'}</p>
          </div>
        )}

        {(step === 'lista' || step === 'subiendo') && info && (
          <>
            <ClipboardList size={32} className="mx-auto mb-2 text-accent-ink" />
            <p className="text-xs font-semibold uppercase tracking-wide text-content-3">Foto de evidencia</p>
            <h1 className="mt-1 text-lg font-bold text-content">{info.title}</h1>
            {info.has_photo && step === 'lista' && (
              <p className="mt-1 text-xs text-amber-600">Ya hay una foto — si subes otra, la reemplaza.</p>
            )}
            {error && <p className="mt-2 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{error}</p>}
            {step === 'subiendo' ? (
              <div className="mt-5 flex flex-col items-center gap-3">
                {preview && <img src={preview} alt="Evidencia" className="max-h-52 rounded-xl border border-line object-contain" />}
                <p className="flex items-center gap-2 text-sm text-content-2"><Loader2 size={15} className="animate-spin" /> Subiendo…</p>
              </div>
            ) : (
              <div className="mt-5 space-y-2">
                <button onClick={() => camRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-on-accent transition hover:opacity-90">
                  <Camera size={17} /> Tomar la foto
                </button>
                <button onClick={() => galRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-line px-4 py-2.5 text-sm font-medium text-content-2 transition hover:text-content">
                  <ImageIcon size={15} /> Elegir de la galería
                </button>
              </div>
            )}
          </>
        )}

        {step === 'exito' && (
          <div className="py-4">
            <CheckCircle2 size={44} className="mx-auto mb-3 text-emerald-500" />
            <h1 className="text-lg font-semibold text-content">¡Foto recibida!</h1>
            <p className="mt-1 text-sm text-content-3">
              Ya aparece en la tarea de la computadora. Puedes cerrar esta página.
            </p>
            {preview && <img src={preview} alt="Evidencia" className="mx-auto mt-4 max-h-52 rounded-xl border border-line object-contain" />}
            <button onClick={() => { setPreview(null); setStep('lista'); }}
              className="mx-auto mt-4 flex items-center gap-1.5 text-sm font-medium text-accent-ink">
              <RefreshCw size={14} /> Tomar otra (reemplaza)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
